import { type FunctionComponent, useMemo } from 'react';
import toNumber from 'lodash/toNumber';
import { type ImageProps } from '@direct-frontend/components';
import { Image } from '@direct-frontend/components';
import { type OpenApiSchemaMediaItem, type OpenApiSchemaMediaItemV2 } from '../../api/schema';
import { mapMediaV2FieldToMediaItemV2, MEDIA_TYPE_VIDEO, type MediaV2Field } from '../../lib/form';
import { getProductImageSources } from '../../lib/images';

export type AdaptiveMediaProps = ImageProps & {
    media: OpenApiSchemaMediaItemV2 | MediaV2Field;
};

export const AdaptiveMedia: FunctionComponent<AdaptiveMediaProps> = ({ media, ...imageProps }) => {
    const { width: imageOriginalWidth } = imageProps;

    const source = useMemo(() => {
        const mediaItem = 'id' in media ? mapMediaV2FieldToMediaItemV2(media) : media;

        if (mediaItem.type !== MEDIA_TYPE_VIDEO && 'file_path' in mediaItem.payload) {
            const sources = getProductImageSources(mediaItem.payload);
            const elementWidth = toNumber(imageOriginalWidth) * (window.devicePixelRatio || 1);
            const source = sources.find(([_, width]) => elementWidth <= width) ?? sources.at(-1);

            return source?.[0];
        }

        if (
            mediaItem.type === MEDIA_TYPE_VIDEO &&
            'first_frame_url' in mediaItem.payload &&
            typeof mediaItem.payload.first_frame_url === 'string'
        ) {
            return mediaItem.payload.first_frame_url;
        }

        return undefined;
    }, [imageOriginalWidth, media]);

    return <Image {...imageProps} src={source} />;
};

export type AdaptiveImageProps = ImageProps & {
    media: OpenApiSchemaMediaItem;
};

export const AdaptiveImage: FunctionComponent<AdaptiveImageProps> = ({ media, ...imageProps }) => {
    const { width: imageOriginalWidth } = imageProps;

    const source = useMemo(() => {
        if (media.type === 'IMAGE') {
            const sources = getProductImageSources(media);
            const elementWidth = toNumber(imageOriginalWidth) * (window.devicePixelRatio || 1);
            const source = sources.find(([_, width]) => elementWidth <= width) ?? sources.at(-1);

            return source?.[0];
        }

        return undefined;
    }, [imageOriginalWidth, media]);

    return <Image {...imageProps} src={source} />;
};
